import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import TournamentCard from '../../components/Portal/TournamentCard';
import { portalTournamentAPI, memberAPI } from '../../services/api';

/**
 * PortalTournamentList - Member's tournament hub (replaces Home as primary landing)
 * Shows current/upcoming tournaments and past tournaments.
 */
const PortalTournamentList = () => {
  const [tournaments, setTournaments] = useState([]);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tournamentsRes, memberRes] = await Promise.all([
          portalTournamentAPI.getMyTournaments(),
          memberId ? memberAPI.getPortalSummary(memberId) : Promise.resolve(null),
        ]);

        setTournaments(tournamentsRes.data?.data?.tournaments || []);
        if (memberRes) {
          setMember(memberRes.data?.data?.member || null);
        }
      } catch (err) {
        console.error('Failed to load tournaments:', err);
        setError('Failed to load your tournaments.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [memberId]);

  const now = new Date();
  const upcoming = tournaments.filter(
    (t) => new Date(t.tournament?.date) >= now || ['signup', 'roster-locked', 'tee-sheet-published', 'gameday'].includes(t.tournament?.portalStatus)
  );
  const past = tournaments.filter(
    (t) => !upcoming.includes(t)
  );

  if (loading) {
    return (
      <PortalLayout title="Tournaments">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </div>
      </PortalLayout>
    );
  }

  if (error) {
    return (
      <PortalLayout title="Tournaments">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Tournaments" member={member}>
      {/* Welcome Header */}
      {member && (
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-5 text-white mb-5 shadow-lg">
          <h2 className="text-xl font-bold mb-1">
            Welcome, {member.firstName || 'Member'}!
          </h2>
          <div className="flex gap-4 mt-3">
            <div className="bg-white/20 rounded-lg px-3 py-2">
              <p className="text-green-100 text-xs uppercase tracking-wide">HCP</p>
              <p className="text-lg font-bold">{member.handicapIndex?.toFixed(1) || '--'}</p>
            </div>
            <div className="bg-white/20 rounded-lg px-3 py-2">
              <p className="text-green-100 text-xs uppercase tracking-wide">GHIN</p>
              <p className="text-lg font-bold">{member.ghin || '--'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current / Upcoming */}
      {upcoming.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 mb-3">Current &amp; Upcoming</h3>
          <div className="space-y-3">
            {upcoming.map((t) => (
              <TournamentCard key={t._id} playerRecord={t} />
            ))}
          </div>
        </div>
      )}

      {/* Past */}
      {past.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 mb-3">Past Tournaments</h3>
          <div className="space-y-2">
            {past.map((t) => (
              <TournamentCard key={t._id} playerRecord={t} compact />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {tournaments.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">&#9971;</div>
          <h3 className="font-medium text-gray-700 mb-1">No Tournaments Yet</h3>
          <p className="text-sm text-gray-500">
            Your upcoming and past tournaments will appear here.
          </p>
        </div>
      )}

      {/* Quick link to settings */}
      <div className="mt-6 text-center">
        <Link to="/portal" className="text-sm text-green-600 hover:text-green-700 font-medium">
          Manage Profile &amp; Settings
        </Link>
      </div>
    </PortalLayout>
  );
};

export default PortalTournamentList;
